import type { TableMetric } from "@/features/analytics/constants";
import type { Column } from "@replo/design-system/components/table/Table";
import type { RangeResult } from "schemas/generated/analyticsRead";

import * as React from "react";

import { TablePageCellContent } from "@editor/components/analytics/TablePageCellContent";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import { formatValueToTableCell } from "@/editor/utils/analytics";
import { useAnalyticsQueryContext } from "@/features/analytics/contexts/AnalyticsQueryContext";
import Table from "@replo/design-system/components/table/Table";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";

const TOP_PAGES_TABLE_METRICS: {
  key: TableMetric;
  label: string;
  tooltipContent: string;
}[] = [
  {
    key: "unique_sessions",
    label: "Unique Sessions",
    tooltipContent: "Unique Sessions Definition",
  },
  {
    key: "conversion_rates",
    label: "Conversion Rate",
    tooltipContent: "Conversion Rate Definition",
  },
];

const PAGE_NAME_COLUMN_WIDTH_CLASSNAME = "w-[280px]";

type TopPagesTableRow = {
  key: string;
  urlPath: string;
  metadata: RangeResult["metadata"];
  metrics: RangeResult["metrics"];
};

type TopPagesTableProps = {
  isLoading: boolean;
  topPages: RangeResult[];
};

export const TopPagesTable = ({ isLoading, topPages }: TopPagesTableProps) => {
  const analytics = useLogAnalytics();
  const { dispatchAnalyticsQuery } = useAnalyticsQueryContext();

  const handleUrlPathClick = React.useCallback(() => {
    analytics("analytics.url.open", {
      source: "analytics_overview",
    });
  }, [analytics]);

  const handlePageTitleClick = React.useCallback(
    (urlPath: string) => {
      dispatchAnalyticsQuery({
        type: "openDeepDivePageFromOverviewPage",
        payload: {
          urlPath,
          source: "analytics_overview",
        },
      });
    },
    [dispatchAnalyticsQuery],
  );

  const tableData: TopPagesTableRow[] = React.useMemo(() => {
    return topPages.map((result) => ({
      key: result.metadata.url,
      urlPath: result.urlPath,
      metadata: result.metadata,
      metrics: result.metrics,
    }));
  }, [topPages]);

  const columns: Column<TopPagesTableRow>[] = React.useMemo(() => {
    const cols: Column<TopPagesTableRow>[] = [
      {
        key: "page",
        header: "Page Name",
        width: PAGE_NAME_COLUMN_WIDTH_CLASSNAME,
        renderCell: (row) => (
          <TablePageCellContent
            title={row.metadata.title}
            url={row.metadata.url}
            urlPath={row.urlPath}
            onPageTitleClick={() => handlePageTitleClick(row.urlPath)}
            onUrlPathClick={handleUrlPathClick}
          />
        ),
      },
    ];

    TOP_PAGES_TABLE_METRICS.forEach((metric) => {
      cols.push({
        key: metric.key,
        header: (
          <Tooltip triggerAsChild content={metric.tooltipContent}>
            <span>{metric.label}</span>
          </Tooltip>
        ),
        renderCell: (row) => {
          const value = row.metrics[metric.key]?.[0];
          return (
            <span className="text-sm">
              {value
                ? formatValueToTableCell({
                    value,
                    name: metric.key,
                    currency: "USD",
                  })
                : "-"}
            </span>
          );
        },
      });
    });

    return cols;
  }, [handlePageTitleClick, handleUrlPathClick]);

  return (
    <Table
      data={tableData}
      columns={columns}
      isLoading={isLoading}
      loadingRows={3}
    />
  );
};
